import type { TransformerMiddleware } from '@blocksuite/store';
import type { Element, ElementContent } from 'hast';

import { HastUtils } from '../utils/hast';

export const NOTION_DATABASE_HOSTS_KEY = 'notionDatabaseHosts';

const NOTION_DATABASE_CLASS = 'collection-content';

/**
 * A database the Notion import creates for one database table of a page. The
 * import allocates it before the page is read; the page holds its host block.
 */
export type NotionDatabaseHost = {
  databaseId: string;
  databaseRootDocId: string;
  databaseStorageVersion: number;
  title: string;
  /** The table the host belongs to, see {@link notionDatabaseTableKey}. */
  tableKey: string;
};

/** Why a Notion export cannot be imported; the import UI names each case. */
export type NotionImportErrorCode =
  | 'markdown-export'
  | 'no-pages'
  | 'database-without-columns'
  | 'database-without-title-column'
  | 'table-without-database'
  | 'table-not-placed'
  | 'duplicate-row'
  | 'relation-without-row-id'
  | 'relation-row-missing'
  | 'relation-multiple-targets'
  | 'relation-without-target'
  | 'attachment-external'
  | 'attachment-missing'
  | 'attachment-invalid'
  | 'invalid-link'
  | 'page-unreadable';

/**
 * A Notion export the import refuses. `subject` names the page, database,
 * column or file the refusal is about.
 */
export class NotionImportError extends Error {
  constructor(
    readonly code: NotionImportErrorCode,
    readonly subject: string,
    message: string
  ) {
    super(message);
    this.name = 'NotionImportError';
  }
}

/** A Notion database is a table with the collection class; others are tables. */
export function isNotionDatabaseTable(element: Element): boolean {
  return (
    element.tagName === 'table' && hasClass(element, NOTION_DATABASE_CLASS)
  );
}

function hasClass(element: Element, className: string): boolean {
  const classes = element.properties?.className;
  return Array.isArray(classes) && classes.includes(className);
}

/**
 * The title Notion shows above a database table: the collection title next to
 * the table in its container. A full page database has none.
 */
export function notionDatabaseTableTitle(
  container: Element | undefined
): string {
  const title = container
    ? HastUtils.getElementChildren(container).find(child =>
        hasClass(child, 'collection-title')
      )
    : undefined;
  return title ? HastUtils.getTextContent(title).trim() : '';
}

/**
 * Splits the database tables out of a Notion block the import keeps as text,
 * as a callout. `text` is the block without its tables and their collection
 * titles; `databases` holds, in document order, each table with those titles
 * in a copy of its container, so the table keeps its key and its title.
 */
export function splitNotionDatabaseTables(element: Element): {
  text: Element;
  databases: Element[];
} {
  const databases: Element[] = [];
  const split = (node: Element): Element => {
    const elements = HastUtils.getElementChildren(node);
    const titles = elements.some(isNotionDatabaseTable)
      ? elements.filter(child => hasClass(child, 'collection-title'))
      : [];
    const children = node.children.flatMap((child): ElementContent[] => {
      if (child.type !== 'element') return [child];
      if (isNotionDatabaseTable(child)) {
        databases.push({ ...node, children: [...titles, child] });
        return [];
      }
      if (titles.includes(child)) return [];
      return [split(child)];
    });
    return { ...node, children };
  };
  return { text: split(element), databases };
}

function elementsByTag(node: Element, tagName: string): Element[] {
  return HastUtils.getElementChildren(node).flatMap(child => [
    ...(child.tagName === tagName ? [child] : []),
    ...elementsByTag(child, tagName),
  ]);
}

/**
 * The identity of a Notion database table in its page: its title, its column
 * names and the Notion ids of its rows. The import plans a database per key,
 * and the page adapter places each host at the table with its key.
 */
export function notionDatabaseTableKey(
  table: Element,
  container: Element | undefined
): string {
  // Whitespace is no identity: HTML normalization may add or drop it.
  const columns = elementsByTag(table, 'th').map(th =>
    HastUtils.getTextContent(th).replace(/\s+/g, '')
  );
  const rows = elementsByTag(table, 'tr')
    .map(tr => tr.properties?.id)
    .filter((id): id is string => typeof id === 'string');
  return JSON.stringify({
    title: notionDatabaseTableTitle(container).replace(/\s+/g, ''),
    columns,
    rows,
  });
}

/** Hands the database hosts planned for one page to its adapter. */
export const notionDatabaseHostsMiddleware = (
  hosts: readonly NotionDatabaseHost[]
): TransformerMiddleware => {
  return ({ adapterConfigs }) => {
    adapterConfigs.set(NOTION_DATABASE_HOSTS_KEY, JSON.stringify(hosts));
  };
};

/** Database hosts the Notion import planned for the page being read. */
export function readNotionDatabaseHosts(
  configs: ReadonlyMap<string, string>
): NotionDatabaseHost[] {
  const value = configs.get(NOTION_DATABASE_HOSTS_KEY);
  return value ? (JSON.parse(value) as NotionDatabaseHost[]) : [];
}
